import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { RealModeToggle } from '@/components/logistics/RealModeToggle';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const fmt = (n) => (n == null ? '—' : `$${Number(n).toLocaleString('es-CO')}`);
export const REFUND_REASONS = {
  cancel_before_dispatch: 'Cancelación antes del despacho', late_delivery: 'Entrega tardía', damaged_or_lost: 'Daño o pérdida',
  overcharge_reweigh: 'Cobro mayor / reliquidación', return_shipping: 'Envío de devolución', goodwill: 'Compensación comercial', other: 'Otro',
};
export const REFUND_STATUS = { requested: 'Solicitado', approved: 'Aprobado', processed: 'Procesado', rejected: 'Rechazado' };
const errMsg = (e) => {
  const d = e.response?.data?.detail;
  return d?.message || (Array.isArray(d) ? String(d[0]?.msg || '').replace(/^Value error,\s*/, '') : d) || 'Operación no válida';
};
const EMPTY = { reason: 'late_delivery', amount_to_buyer: '', return_shipping_cost: '', seller_amount: '', automarket_amount: '', carrier_amount: '', note: '' };

const NewRefund = ({ summary, onDone }) => {
  const [f, setF] = useState(EMPTY);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const n = (v) => Number(v || 0);
  const total = n(f.amount_to_buyer) + n(f.return_shipping_cost);
  const split = n(f.seller_amount) + n(f.automarket_amount) + n(f.carrier_amount);
  const save = async () => {
    try {
      await api.post('/admin/shipping-refunds', {
        order_id: summary.order_id, reason: f.reason, note: f.note, idempotency_key: `ui_${summary.order_id}_${Date.now()}`,
        amount_to_buyer: n(f.amount_to_buyer), return_shipping_cost: n(f.return_shipping_cost),
        seller_amount: n(f.seller_amount), automarket_amount: n(f.automarket_amount), carrier_amount: n(f.carrier_amount),
      });
      toast.success('Reembolso de envío registrado'); setF(EMPTY); onDone();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="mt-4 border border-slate-200 p-4" data-testid="shipping-refund-form">
      <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
        <select value={f.reason} onChange={set('reason')} className="h-9 border border-slate-300 bg-white px-2 text-sm" data-testid="srf-reason-select">
          {Object.entries(REFUND_REASONS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        {[['amount_to_buyer', 'Reembolso al comprador'], ['return_shipping_cost', 'Costo envío de retorno'], ['seller_amount', 'Asume vendedor'],
          ['automarket_amount', 'Asume AutoMarket'], ['carrier_amount', 'Asume transportadora']].map(([k, l]) => (
          <Input key={k} type="number" min="0" placeholder={`${l} (COP)`} value={f[k]} onChange={set(k)} className="h-9 rounded-none text-sm" data-testid={`srf-${k}`} />
        ))}
      </div>
      <Input placeholder="Motivo detallado (obligatorio)" value={f.note} onChange={set('note')} className="mt-2 h-9 rounded-none text-sm" data-testid="srf-note" />
      <p className={`mt-1 text-xs ${total === split ? 'text-emerald-700' : 'text-rose-700'}`} data-testid="srf-split-check">
        Total {fmt(total)} · Repartido {fmt(split)} {total === split ? '✓' : '(debe coincidir)'} · Disponible para reembolsar {fmt(summary.available_to_refund)}
      </p>
      <Button size="sm" className="mt-2 rounded-none bg-slate-900" onClick={save} data-testid="srf-save-btn">Registrar reembolso</Button>
    </div>
  );
};

const RefundRow = ({ r, onChange }) => {
  const act = async (status) => {
    let gateway_ref = null;
    let note = null;
    if (status === 'processed') { gateway_ref = window.prompt('Referencia de la devolución en la pasarela o banco'); if (!gateway_ref) return; }
    if (status === 'rejected') { note = window.prompt('Motivo del rechazo (se le informará al comprador)'); if (!note) return; }
    try { await api.post(`/admin/shipping-refunds/${r.shipping_refund_id}/status`, { status, gateway_ref, note }); toast.success('Estado actualizado'); onChange(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <tr className="border-t border-slate-100" data-testid={`srf-row-${r.shipping_refund_id}`}>
      <td className="py-1 pr-3">{REFUND_REASONS[r.reason]}</td><td className="pr-3">{fmt(r.amount_to_buyer)}</td><td className="pr-3">{fmt(r.return_shipping_cost)}</td>
      <td className="pr-3">V {fmt(r.seller_amount)} · AM {fmt(r.automarket_amount)} · T {fmt(r.carrier_amount)}</td>
      <td className="pr-3" data-testid={`srf-status-${r.shipping_refund_id}`}>{REFUND_STATUS[r.status]}</td>
      <td className="whitespace-nowrap">
        {r.status === 'requested' && <Button size="sm" variant="ghost" onClick={() => act('approved')} data-testid={`srf-approve-${r.shipping_refund_id}`}>Aprobar</Button>}
        {r.status === 'approved' && <Button size="sm" variant="ghost" onClick={() => act('processed')} data-testid={`srf-process-${r.shipping_refund_id}`}>Procesar</Button>}
        {['requested', 'approved'].includes(r.status) && <Button size="sm" variant="ghost" onClick={() => act('rejected')} data-testid={`srf-reject-${r.shipping_refund_id}`}>Rechazar</Button>}
      </td>
    </tr>
  );
};

const OrderPanel = ({ data, reload }) => {
  const s = data.summary;
  const pj = data.payout_projection;
  return (
    <div className="mt-4" data-testid="srf-order-panel">
      <p className="text-sm" data-testid="srf-order-summary">
        Pedido <b className="font-mono">{s.order_id}</b> · Estado {s.order_status} · Envío pagado {fmt(s.shipping_paid_by_buyer)} · Comprometido {fmt(s.committed_to_buyer)} · Disponible <b>{fmt(s.available_to_refund)}</b>
      </p>
      {s.full_order_refund && <p className="text-xs text-rose-700">Este pedido ya tiene un reembolso total registrado.</p>}
      {s.subsidy && <p className="text-xs text-slate-500">Subsidio de envío: {s.subsidy.status} · {fmt(s.subsidy.discount)}</p>}
      {pj && <p className="text-xs text-slate-500" data-testid="srf-payout-projection">Desembolso real {fmt(pj.actual_net)} · Deducciones al vendedor (procesadas) {fmt(pj.seller_deductions)} · Proyectado {fmt(pj.projected_net)} {s.payout_already_paid ? '· ya pagado: se recuperaría de próximos desembolsos' : ''}</p>}
      <table className="mt-3 w-full text-xs">
        <thead className="text-left text-slate-500"><tr><th className="py-1 pr-3">Motivo</th><th className="pr-3">Al comprador</th><th className="pr-3">Retorno</th><th className="pr-3">Reparto</th><th className="pr-3">Estado</th><th /></tr></thead>
        <tbody>{data.items.map((r) => <RefundRow key={r.shipping_refund_id} r={r} onChange={reload} />)}</tbody>
      </table>
      {!s.full_order_refund && <NewRefund summary={s} onDone={reload} />}
    </div>
  );
};

export default function AdminShippingRefunds() {
  const [orderId, setOrderId] = useState('');
  const [data, setData] = useState(null);
  const [list, setList] = useState({ items: [], totals: {} });
  const loadList = useCallback(() => api.get('/admin/shipping-refunds').then(({ data: d }) => setList(d)).catch(() => {}), []);
  const loadOrder = async (oid = orderId) => {
    try { const { data: d } = await api.get(`/admin/shipping-refunds/order/${oid.trim()}`); setData(d); }
    catch (e) { setData(null); toast.error(errMsg(e)); }
  };
  useEffect(() => { loadList(); }, [loadList]);
  const reload = () => { loadOrder(); loadList(); };
  const proc = list.totals.processed;

  return (
    <Layout>
      <div className="mx-auto max-w-6xl px-4 py-10 pb-24 sm:px-6 lg:px-8" data-testid="admin-shipping-refunds-page">
        <Link to="/admin/configuracion/envios" className="text-xs font-bold uppercase tracking-[0.25em] text-orange-600">Envíos y logística</Link>
        <h1 className="mt-1 font-display text-3xl font-black tracking-tighter text-slate-900">Devoluciones y reembolsos de envío</h1>
        <RealModeToggle getUrl="/admin/shipping-refunds-settings" field="apply_to_payouts" label="Deducciones reales a desembolsos"
          testid="srf-shadow-banner"
          onDesc="Deducciones reales activas: la parte del vendedor en cada reembolso procesado se descuenta de su desembolso (retenido si está pendiente). La devolución al comprador sigue el proceso manual."
          offDesc="Los reembolsos se registran y se reparten entre vendedor, AutoMarket y transportadora. El descuento al desembolso del vendedor se calcula pero no se aplica."
          save={(on) => api.put('/admin/shipping-refunds-settings', { apply_to_payouts: on })} />
        <section className="mt-6 border border-slate-200 bg-white p-5">
          <h2 className="font-display text-lg font-bold">Buscar pedido</h2>
          <div className="mt-2 flex gap-2">
            <Input placeholder="ID del pedido" value={orderId} onChange={(e) => setOrderId(e.target.value)} className="w-72 rounded-none" data-testid="srf-order-input" />
            <Button className="rounded-none bg-slate-900" onClick={() => loadOrder()} data-testid="srf-order-search">Consultar</Button>
          </div>
          {data && <OrderPanel data={data} reload={reload} />}
        </section>
        <section className="mt-6 border border-slate-200 bg-white p-5" data-testid="srf-list">
          <h2 className="font-display text-lg font-bold">Registro de reembolsos de envío</h2>
          <p className="mt-1 text-xs text-slate-500" data-testid="srf-totals">
            Procesados: {proc?.count || 0} · Al comprador {fmt(proc?.amount_to_buyer || 0)} · Vendedores {fmt(proc?.seller_amount || 0)} · AutoMarket {fmt(proc?.automarket_amount || 0)} · Transportadoras {fmt(proc?.carrier_amount || 0)}
          </p>
          <table className="mt-2 w-full text-xs">
            <thead className="text-left text-slate-500"><tr><th className="py-1 pr-3">Pedido</th><th className="pr-3">Motivo</th><th className="pr-3">Al comprador</th><th className="pr-3">Estado</th><th>Fecha</th></tr></thead>
            <tbody>
              {list.items.map((r) => (
                <tr key={r.shipping_refund_id} className="cursor-pointer border-t border-slate-100 hover:bg-slate-50" onClick={() => { setOrderId(r.order_id); loadOrder(r.order_id); }}>
                  <td className="py-1 pr-3 font-mono">{r.order_id}</td><td className="pr-3">{REFUND_REASONS[r.reason]}</td><td className="pr-3">{fmt(r.amount_to_buyer)}</td>
                  <td className="pr-3">{REFUND_STATUS[r.status]}</td><td>{(r.created_at || '').slice(0, 10)}</td>
                </tr>
              ))}
              {!list.items.length && <tr><td colSpan={5} className="py-3 text-slate-400" data-testid="srf-empty">Sin reembolsos de envío.</td></tr>}
            </tbody>
          </table>
        </section>
      </div>
    </Layout>
  );
}
